import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { CheckoutApi } from "../api/types";
import { DEFAULT_CONFIG, type CheckoutConfig } from "./config";
import { cssFor, fetchConfig, persistConfig } from "./load";
import { applyBrandCss } from "./tokens";

const BrandContext = createContext<CheckoutConfig | null>(null);

/**
 * Holds the operator config. Starts from what boot resolved (cache, fresh fetch or defaults) and,
 * when that came from the cache, revalidates once in the background.
 */
export function BrandProvider({
  api,
  initial,
  revalidate,
  children,
}: {
  api: CheckoutApi;
  initial: CheckoutConfig;
  revalidate: boolean;
  children: ReactNode;
}) {
  const [config, setConfig] = useState(initial);

  useEffect(() => {
    if (!revalidate) return;
    let live = true;
    void fetchConfig(api, 8000).then((next) => {
      if (!live || !next) return;
      persistConfig(next);
      setConfig(next);
    });
    return () => {
      live = false;
    };
  }, [api, revalidate]);

  useEffect(() => {
    applyBrandCss(cssFor(config));
  }, [config]);

  return <BrandContext.Provider value={config}>{children}</BrandContext.Provider>;
}

/** The operator config; documented defaults outside a provider (tests, older servers). */
export function useBrand(): CheckoutConfig {
  return useContext(BrandContext) ?? DEFAULT_CONFIG;
}
